import type {
  AssessmentDelivery,
  AssessmentResult,
  AssessmentSubmission,
  AssessmentScope,
} from "@peer-start/platform-contracts";
import { Button, Card } from "@peer-start/ui";
import { ArrowLeft, ArrowRight, CheckCircle2, HelpCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { submitAssessment } from "../api/client.js";
import type { AssessmentResponse } from "../types/assessment-run.js";

export function AssessmentQuestion({
  delivery,
  scope = "topic",
  onComplete,
}: {
  delivery: AssessmentDelivery;
  scope?: AssessmentScope;
  onComplete: (result: AssessmentResult) => void;
}) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const [responses, setResponses] = useState<Record<string, AssessmentResponse>>({});
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [attemptId, setAttemptId] = useState(() => crypto.randomUUID());
  const submission = useRef<AssessmentSubmission | null>(null);
  const timing = useRef<Record<string, number>>({});
  const started = useRef(Date.now());
  const heading = useRef<HTMLHeadingElement>(null);
  const bank = delivery.definition;
  const item = bank.items[index]!;
  const response = responses[item.id];
  useEffect(() => {
    heading.current?.focus();
  }, [index]);
  const recordTime = () => {
    timing.current[item.id] = (timing.current[item.id] ?? 0) + Date.now() - started.current;
    started.current = Date.now();
  };
  const navigate = (next: number) => {
    recordTime();
    setIndex(next);
    setFailure(null);
  };
  const changeResponse = (value: AssessmentResponse) => {
    if (submission.current) setAttemptId(crypto.randomUUID());
    submission.current = null;
    setResponses((current) => ({ ...current, [item.id]: value }));
  };
  const submit = async () => {
    recordTime();
    setPending(true);
    setFailure(null);
    try {
      submission.current ??= {
        attemptId,
        scope,
        revision: delivery.revision,
        answers: bank.items.map((question) => ({
          itemId: question.id,
          choice: responses[question.id]!.choice,
          confidence: responses[question.id]!.confidence!,
          timeMs: timing.current[question.id] ?? 0,
        })),
      };
      const result = await submitAssessment(bank.id, submission.current);
      onComplete(result);
    } catch (error) {
      setFailure(error instanceof Error ? error.message : t("common.error"));
    } finally {
      setPending(false);
    }
  };
  return (
    <Card>
      <div className="mb-5 flex flex-wrap justify-between gap-2 text-sm text-muted-foreground">
        <span>
          {t("knowledgeAssessment.questionProgress", { current: index + 1, total: bank.items.length })}
        </span>
        <span>{bank.skills.find((skill) => skill.id === item.skillId)?.title}</span>
      </div>
      <progress
        className="mb-7 h-2 w-full accent-primary"
        aria-label={t("knowledgeAssessment.progress")}
        value={index + 1}
        max={bank.items.length}
      />
      {item.case ? (
        <p className="mb-5 rounded-xl bg-muted/30 p-4 text-base leading-relaxed">{item.case}</p>
      ) : null}
      <h2
        ref={heading}
        tabIndex={-1}
        className="text-xl font-medium focus:outline-none"
        id="assessment-question"
      >
        {item.prompt}
      </h2>
      <fieldset className="mt-5 space-y-3" disabled={pending} aria-labelledby="assessment-question">
        {item.options.map((option, optionIndex) => (
          <label
            key={optionIndex}
            className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors focus-within:ring-3 focus-within:ring-ring/30 ${response?.choice === optionIndex ? "border-primary bg-primary/5" : "border-border hover:bg-muted/30"}`}
          >
            <input
              className="mt-1 accent-primary"
              type="radio"
              name={`answer-${item.id}`}
              data-control-id={`assessment.answer.${optionIndex}`}
              value={optionIndex}
              checked={response?.choice === optionIndex}
              onChange={() => changeResponse({ choice: optionIndex, confidence: null })}
            />
            <span className="font-medium text-primary">{String.fromCharCode(65 + optionIndex)}</span>
            <span className="text-sm leading-relaxed">{option}</span>
          </label>
        ))}
      </fieldset>
      <fieldset className="mt-7" disabled={pending || !response}>
        <legend className="mb-3 font-medium">{t("knowledgeAssessment.certaintyQuestion")}</legend>
        <div className="grid gap-3 sm:grid-cols-2">
          {(["certain", "doubtful"] as const).map((confidence) => {
            const Icon = confidence === "certain" ? CheckCircle2 : HelpCircle;
            return (
              <label
                key={confidence}
                className={`flex cursor-pointer gap-3 rounded-xl border p-4 focus-within:ring-3 focus-within:ring-ring/30 ${!response ? "opacity-50" : ""} ${response?.confidence === confidence ? "border-primary bg-primary/5" : "border-border"}`}
              >
                <input
                  className="accent-primary"
                  type="radio"
                  name={`confidence-${item.id}`}
                  data-control-id={`assessment.confidence.${confidence}`}
                  checked={response?.confidence === confidence}
                  onChange={() => changeResponse({ ...response!, confidence })}
                />
                <Icon aria-hidden="true" className="size-5 shrink-0 text-primary" />
                <span>
                  <strong className="text-sm">{t(`knowledgeAssessment.confidence.${confidence}`)}</strong>
                  <span className="mt-1 block text-xs text-muted-foreground">
                    {t(`knowledgeAssessment.confidenceHint.${confidence}`)}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>
      {failure ? (
        <p role="alert" className="mt-4 text-sm text-destructive-foreground">
          {failure}
        </p>
      ) : null}
      <div className="mt-7 flex justify-between gap-3 border-t border-border pt-5">
        <Button
          controlId="assessment.previous"
          variant="secondary"
          disabled={pending || index === 0}
          onClick={() => navigate(index - 1)}
        >
          <ArrowLeft aria-hidden="true" className="size-4" />
          {t("knowledgeAssessment.previous")}
        </Button>
        <Button
          controlId="assessment.next"
          disabled={pending || !response?.confidence}
          onClick={() => (index === bank.items.length - 1 ? void submit() : navigate(index + 1))}
        >
          {t(
            pending
              ? "knowledgeAssessment.saving"
              : index === bank.items.length - 1
                ? "knowledgeAssessment.finish"
                : "knowledgeAssessment.next",
          )}
          <ArrowRight aria-hidden="true" className="size-4" />
        </Button>
      </div>
    </Card>
  );
}
